import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { UserAuthenticationService } from '../services/user-authentication.service';

/**
 * Static files bundled with the app — `assets/i18n/translations.json` and friends.
 *
 * <p>These are served by whatever hosts the app, not by an API, so a bearer token buys nothing and costs
 * something: in a deployed environment /assets sits behind a CDN, and this would hand it a live JWT. The
 * Content-Type header added below is equally pointless on a GET for a file.</p>
 *
 * <p>Matched on the resolved same-origin path rather than "does the URL contain assets/", because the Asset
 * API is a real backend here (environment.proxyUrls.assetApi) — a route like `…/api/v1/assets/{id}` must keep
 * its token. Being cross-origin, it can never match this.</p>
 */
const isStaticAsset = (url: string): boolean => {
  try {
    const resolved = new URL(url, window.location.origin);
    return resolved.origin === window.location.origin && resolved.pathname.startsWith('/assets/');
  } catch {
    return false;
  }
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const userAuthenticationService = inject(UserAuthenticationService);
  const authToken = userAuthenticationService.getStoredJwtToken();
  const excludedUrls = ['/login', '/register', 'wft-geo-db.p.rapidapi.com'];
  const isExcluded = excludedUrls.some((url) => req.url.includes(url));

  if (isExcluded || isStaticAsset(req.url)) {
    return next(req);
  }

  const headers: Record<string, string> = {
    Authorization: authToken ? `Bearer ${authToken}` : '',
  };

  if (!(req.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const authReq = req.clone({
    setHeaders: headers,
  });

  return next(authReq);
};
